/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaCreation.ts
 * FILE_ID: JOBHUB-QA-CREATION-HOOK-001
 * PURPOSE: Own quote selection, classification, preview, and QA creation.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, type QueryClient } from '@tanstack/react-query'
import {
  isQaSourceSelectable,
  nextDefaultQaNames,
  provenanceFromPreview,
  qaPlanSelectionStorageKey,
  qaScopeName,
  type QaApi,
  type QaCreateMode,
  type QaGenerationProvenance,
  type QaInspectionSummary,
  type QaWriteTarget,
} from '../../qa/qaApi'
import { useQaPlanPreview } from '../../qa/useQaPlanPreview'
import {
  suggestClassification,
  type JobClassificationContext,
  type QaClassification,
} from '../../qa/classification'
import type { QuoteRow } from '../../jobDetailTypes'
import { qaErrorMessage } from './qaTabSelectors'
import { invalidateQaExecution } from './useQaWorkspace'
import { requestQaPhonePage } from './phone/qaPhonePage'

const STALE_QUOTE_PREVIEW = 'Quote source changed after preview; refresh before creating QA.'

function planSourceIds(plan: { sources: { quote_sheet_id: number }[] }): number[] {
  return [...new Set(plan.sources.map((source) => source.quote_sheet_id))]
}

function planMatchesTicks(
  plan: { sources: { quote_sheet_id: number }[] },
  selected: number[],
): boolean {
  return planSourceIds(plan).every((id) => selected.includes(id))
}

export function useQaCreation({
  api,
  inspections,
  jobContext,
  jobId,
  quotes = [],
  queryClient,
  selectInspection,
  openCreatedInspection,
  setError,
  activeInspectionId = null,
}: {
  api: QaApi
  inspections: QaInspectionSummary[]
  jobContext?: JobClassificationContext
  jobId: number
  quotes?: QuoteRow[]
  queryClient: QueryClient
  selectInspection: (inspectionId: string | null) => void
  openCreatedInspection?: (inspectionId: string) => void
  setError: (error: string | null) => void
  activeInspectionId?: string | null
}) {
  const carriedSelectionRef = useRef(false)
  const [selectedSheetIds, setSelectedSheetIds] = useState<number[]>(() => {
    if (typeof window === 'undefined') return []
    const key = qaPlanSelectionStorageKey(jobId)
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return []
    window.sessionStorage.removeItem(key)
    try {
      const ids = JSON.parse(raw)
      carriedSelectionRef.current = true
      return Array.isArray(ids) ? ids.filter((id) => Number.isInteger(id)) : []
    } catch {
      return []
    }
  })
  const [createMode, setCreateMode] = useState<QaCreateMode>('combine')
  const [writeTarget, setWriteTarget] = useState<QaWriteTarget>('create')
  const [scopeName, setScopeName] = useState('')
  const [createProgress, setCreateProgress] = useState<{ current: number; total: number } | null>(null)
  const [includeQuoteDerivedItems, setIncludeQuoteDerivedItems] = useState(true)
  const [useTemplates, setUseTemplates] = useState(false)
  const [createNotice, setCreateNotice] = useState<string | null>(null)
  const autoTickedRef = useRef(false)
  useEffect(() => {
    if (autoTickedRef.current || carriedSelectionRef.current || !quotes.length) return
    autoTickedRef.current = true
    if (selectedSheetIds.length > 0) return
    const approved = quotes
      .filter((quote) => quote.status !== 'archived' && isQaSourceSelectable(quote.status))
      .map((quote) => quote.id)
    if (approved.length) setSelectedSheetIds(approved)
  }, [quotes, selectedSheetIds.length])

  // Carried selection restore only — do not open the review panel (Create runs immediately).
  useEffect(() => {
    if (!carriedSelectionRef.current || selectedSheetIds.length === 0) return
    carriedSelectionRef.current = false
  }, [selectedSheetIds.length])

  const explicitlyIncludedSourceIds: string[] = []

  const withProvenance = (
    plan: Parameters<typeof provenanceFromPreview>[0],
    useTemplatesNow: boolean,
    merge: boolean,
  ): QaGenerationProvenance => ({
    ...provenanceFromPreview(plan),
    ...(explicitlyIncludedSourceIds.length
      ? { explicitly_included_source_ids: explicitlyIncludedSourceIds }
      : {}),
    include_quote_derived_items: includeQuoteDerivedItems,
    ...(useTemplatesNow ? { use_templates: true } : {}),
    ...(merge && activeInspectionId ? { inspection_id: activeInspectionId } : {}),
    ...(!merge && activeInspectionId ? { copy_from_inspection_id: activeInspectionId } : {}),
    allow_duplicate_source: true,
  })

  const suggestion = useMemo(() => suggestClassification(jobContext), [jobContext])
  const [classification, setClassification] = useState<QaClassification>(() => ({
    category: suggestion.category,
    projectType: suggestion.projectType,
    buildingClasses: suggestion.buildingClasses,
    tradeTags: suggestion.tradeTags,
  }))
  const classificationTouched = useRef(false)
  const lastSuggestionRef = useRef(suggestion.reason)
  useEffect(() => {
    if (classificationTouched.current || lastSuggestionRef.current === suggestion.reason) return
    lastSuggestionRef.current = suggestion.reason
    setClassification({
      category: suggestion.category,
      projectType: suggestion.projectType,
      buildingClasses: suggestion.buildingClasses,
      tradeTags: suggestion.tradeTags,
    })
  }, [suggestion])
  const handleClassificationChange = (next: QaClassification) => {
    classificationTouched.current = true
    setClassification(next)
  }

  const visibleQuoteIds = useMemo(() => {
    if (quotes.length === 0) return null
    return new Set(quotes.filter((quote) => !quote.hidden_from_qa).map((quote) => quote.id))
  }, [quotes])
  const effectiveSelectedIds = useMemo(
    () => (visibleQuoteIds
      ? selectedSheetIds.filter((id) => visibleQuoteIds.has(id))
      : selectedSheetIds),
    [selectedSheetIds, visibleQuoteIds],
  )
  const effectiveRef = useRef(effectiveSelectedIds)
  effectiveRef.current = effectiveSelectedIds
  useEffect(() => {
    if (!visibleQuoteIds || effectiveSelectedIds.length === selectedSheetIds.length) return
    setSelectedSheetIds(effectiveSelectedIds)
    if (typeof window === 'undefined') return
    const key = qaPlanSelectionStorageKey(jobId)
    if (effectiveSelectedIds.length) window.sessionStorage.setItem(key, JSON.stringify(effectiveSelectedIds))
    else window.sessionStorage.removeItem(key)
  }, [effectiveSelectedIds, jobId, selectedSheetIds.length, visibleQuoteIds])

  const planPreview = useQaPlanPreview({
    api,
    quoteSheetIds: effectiveSelectedIds,
    mode: createMode,
    inspections,
    explicitlyIncludedSourceIds,
  })

  const create = useMutation({
    mutationFn: async (vars: { useTemplates: boolean }): Promise<{
      createdIds: string[]
      skipped: string[]
    }> => {
      setCreateNotice(null)
      const chosenIds = effectiveRef.current
      if (chosenIds.length === 0) return { createdIds: [], skipped: [] }
      const preview = await api.previewQaPlan(chosenIds, createMode, explicitlyIncludedSourceIds)
      const skipped = (preview.skipped ?? []).map((row) => row.reason).filter(Boolean)
      if (!preview || preview.plans.length === 0) {
        throw new Error(skipped.join(' ') || 'Review the accepted quote preview before creating QA.')
      }
      let retried = false
      const postPlan = async (
        plan: (typeof preview.plans)[number],
        useTemplatesNow: boolean,
        merge: boolean,
      ) => {
        if (!planMatchesTicks(plan, chosenIds)) return null
        const ids = planSourceIds(plan)
        if (ids.length === 0) return null
        const provenance = withProvenance(plan, useTemplatesNow, merge)
        const send = (sheetIds: number[], posted: QaGenerationProvenance) =>
          sheetIds.length === 1
            ? api.generateFromQuoteSheet(sheetIds[0], classification, posted)
            : api.generateFromQuoteSheets(sheetIds, classification, posted)
        try {
          return await send(ids, provenance)
        } catch (error) {
          if (retried || qaErrorMessage(error, '') !== STALE_QUOTE_PREVIEW) throw error
          retried = true
          const again = await api.previewQaPlan(
            chosenIds,
            createMode,
            explicitlyIncludedSourceIds,
          )
          const next = again.plans.find((candidate) => planMatchesTicks(candidate, chosenIds))
          if (!next) throw error
          const nextIds = planSourceIds(next)
          if (nextIds.length === 0) throw error
          return send(nextIds, withProvenance(next, useTemplatesNow, merge))
        }
      }
      const keepName = async (inspectionId: string, name: string | null) => {
        if (!name) return
        try {
          await api.renameInspection(inspectionId, name)
        } catch {
          /* keep the generic generator name */
        }
      }
      const base = scopeName.trim()
      const existingNames = inspections.map((row) => row.inspection_name ?? '')
      const mergingOpen = (writeTarget === 'replace' || writeTarget === 'add') && Boolean(activeInspectionId)
      if (writeTarget === 'replace' && activeInspectionId) {
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/templates/qaTemplateWriteTarget.ts (resolveTemplateInsertTarget)
        await api.clearInspectionStructure(activeInspectionId)
      }
      if (createMode === 'combine') {
        const inspection = await postPlan(preview.plans[0], vars.useTemplates, mergingOpen)
        if (!inspection) return { createdIds: [], skipped }
        const renameTo = base || (mergingOpen ? null : nextDefaultQaNames(existingNames, 1, jobId)[0])
        await keepName(inspection.id, renameTo)
        return { createdIds: [inspection.id], skipped }
      }

      const existingCount = inspections.length
      const blankNames = base
        ? []
        : nextDefaultQaNames(
          existingNames,
          mergingOpen ? Math.max(preview.plans.length - 1, 0) : preview.plans.length,
          jobId,
        )
      let blankCursor = 0
      const created: string[] = []
      for (let index = 0; index < preview.plans.length; index++) {
        const merge = mergingOpen && index === 0
        setCreateProgress({ current: index + 1, total: preview.plans.length })
        const inspection = await postPlan(preview.plans[index], vars.useTemplates, merge)
        if (!inspection) continue
        const renameTo = base
          // Reused from: packages/job-hub/src/jobs/qa/qaApiHelpers.ts (qaScopeName)
          ? qaScopeName(base, existingCount + created.length + 1)
          : merge
            ? null
            : blankNames[blankCursor] ?? null
        if (!base && !merge) blankCursor += 1
        await keepName(inspection.id, renameTo)
        created.push(inspection.id)
      }
      return { createdIds: created, skipped }
    },
    onSuccess: ({ createdIds, skipped }) => {
      setError(null)
      setSelectedSheetIds([])
      setScopeName('')
      setCreateProgress(null)
      planPreview.closeReview()
      setCreateNotice(
        skipped.length
          ? `${skipped.length} quote${skipped.length === 1 ? '' : 's'} skipped: ${skipped.join(' ')}`
          : null,
      )
      if (createdIds.length > 0) {
        ;(openCreatedInspection ?? selectInspection)(createdIds[0])
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhonePage.ts (requestQaPhonePage)
        if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches) {
          requestQaPhonePage('qa', true)
        }
      }
      invalidateQaExecution(queryClient, jobId)
    },
    onError: (error: unknown) => {
      setCreateProgress(null)
      const response = (error as {
        response?: { status?: number; data?: { code?: string; existing_inspection_id?: string } }
      }).response
      const existingId = response?.data?.existing_inspection_id
      if (response?.status === 409 && response.data?.code === 'qa_plan_already_exists' && existingId) {
        ;(openCreatedInspection ?? selectInspection)(existingId)
        setError(null)
        setCreateNotice('This QA already exists. Opened it.')
        return
      }
      const message = qaErrorMessage(error, 'Failed to create QA from the selected quotes.')
      setError(message.includes('This quote has no lines.') ? 'This quote has no lines.' : message)
    },
  })
  const createRequestRef = useRef(false)
  const confirmCreate = (options?: { useTemplates?: boolean }) => {
    if (createRequestRef.current || create.isPending) return
    if (planPreview.previewLoading) return
    const plans = planPreview.preview?.plans ?? []
    if (plans.some((plan) => !planMatchesTicks(plan, effectiveRef.current))) return
    createRequestRef.current = true
    create.mutate(
      { useTemplates: options?.useTemplates ?? useTemplates },
      {
        onSettled: () => {
          createRequestRef.current = false
        },
      },
    )
  }
  const toggleSheet = (id: number) => {
    setCreateNotice(null)
    setSelectedSheetIds((current) =>
      current.includes(id) ? current.filter((sheetId) => sheetId !== id) : [...current, id],
    )
  }

  // Re-run an import that failed. The error lives on the quote row, so the
  // quotes query is what has to refetch — the retry either replaces the
  // sentinel with a real sheet or updates it with a new reason.
  const [retryingImportId, setRetryingImportId] = useState<number | null>(null)
  // Keyed by row: the spinner id is cleared on settle, so an error that only
  // carried a message would be attached to nothing and never render.
  const [retryImportError, setRetryImportError] = useState<
    { id: number; message: string } | null
  >(null)
  const retryImport = useMutation({
    mutationFn: (quoteSheetId: number) => api.retryQuoteIngest(quoteSheetId),
    onMutate: (quoteSheetId: number) => {
      setRetryingImportId(quoteSheetId)
      setRetryImportError(null)
    },
    onSuccess: () => {
      setRetryImportError(null)
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
    onError: (error: unknown, quoteSheetId: number) => {
      setRetryImportError({
        id: quoteSheetId,
        message: qaErrorMessage(error, 'Could not start the retry.'),
      })
    },
    onSettled: () => setRetryingImportId(null),
  })

  return {
    retryImport: (quoteSheetId: number) => retryImport.mutate(quoteSheetId),
    retryImportError,
    retryingImportId,
    classification,
    confirmCreate,
    create,
    createMode,
    createNotice,
    createProgress,
    handleClassificationChange,
    includeQuoteDerivedItems,
    planPreview,
    scopeName,
    selectedSheetIds: effectiveSelectedIds,
    setCreateMode,
    setIncludeQuoteDerivedItems,
    setWriteTarget,
    setScopeName,
    setUseTemplates,
    suggestion,
    toggleSheet,
    writeTarget,
  }
}

export type QaCreationController = ReturnType<typeof useQaCreation>
